import { LucideIcon } from "lucide-react";
import { useEffect, useState, useRef } from "react";

interface ColorfulStatCardProps {
  value: string;
  label: string;
  icon: LucideIcon;
  color: "green" | "blue" | "gold" | "purple" | "teal";
  delay?: number;
}

const colorStyles = {
  green: {
    bg: "bg-gradient-to-br from-eco-green/20 to-eco-green/5",
    iconBg: "bg-gradient-to-br from-eco-green to-eco-green-light",
    text: "text-eco-green",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--eco-green)/0.4)]",
    border: "border-eco-green/20 hover:border-eco-green/40",
  },
  blue: {
    bg: "bg-gradient-to-br from-sky-blue/20 to-sky-blue/5",
    iconBg: "bg-gradient-to-br from-sky-blue to-sky-blue-light",
    text: "text-sky-blue",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--sky-blue)/0.4)]",
    border: "border-sky-blue/20 hover:border-sky-blue/40",
  },
  gold: {
    bg: "bg-gradient-to-br from-primary/20 to-amber-glow/5",
    iconBg: "bg-gradient-to-br from-primary to-amber-glow",
    text: "text-primary",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--primary)/0.4)]",
    border: "border-primary/20 hover:border-primary/40",
  },
  purple: {
    bg: "bg-gradient-to-br from-purple/20 to-purple/5",
    iconBg: "bg-gradient-to-br from-purple to-rose",
    text: "text-purple",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--purple)/0.4)]",
    border: "border-purple/20 hover:border-purple/40",
  },
  teal: {
    bg: "bg-gradient-to-br from-teal-bright/20 to-teal-bright/5",
    iconBg: "bg-gradient-to-br from-teal-bright to-eco-green",
    text: "text-teal-bright",
    glow: "group-hover:shadow-[0_0_30px_hsl(var(--teal-bright)/0.4)]",
    border: "border-teal-bright/20 hover:border-teal-bright/40",
  },
};

const ColorfulStatCard = ({ value, label, icon: Icon, color, delay = 0 }: ColorfulStatCardProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [displayValue, setDisplayValue] = useState("0");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  useEffect(() => {
    if (!isVisible) return;

    const numericValue = parseInt(value.replace(/[^0-9]/g, ""));
    const suffix = value.replace(/[0-9]/g, "");
    
    if (isNaN(numericValue)) {
      setDisplayValue(value);
      return;
    }

    const duration = 2000;
    const steps = 60;
    const increment = numericValue / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current = Math.min(Math.round(increment * step), numericValue);
      setDisplayValue(current + suffix);
      
      if (step >= steps) {
        clearInterval(timer);
        setDisplayValue(value);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, value]);

  const styles = colorStyles[color];

  return (
    <div
      ref={ref}
      className={`group relative p-6 rounded-2xl ${styles.bg} border ${styles.border} transition-all duration-500 hover:-translate-y-1 ${styles.glow} overflow-hidden`}
    >
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className={`absolute top-0 right-0 w-32 h-32 rounded-full ${styles.iconBg} blur-3xl opacity-20 group-hover:opacity-40 transition-opacity`} />
      </div>

      <div className="relative z-10 text-center">
        <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${styles.iconBg} flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="w-8 h-8 text-white" />
        </div>
        <div className={`text-3xl md:text-4xl font-bold ${styles.text} mb-2 transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {displayValue}
        </div>
        <div className="text-sm text-muted-foreground font-medium">{label}</div>
      </div>
    </div>
  );
};

export default ColorfulStatCard;
